// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { buildApiPath } from '@/constants';
import type {
  API,
  ConfirmReviewParam,
  ProcessReviewFlowWithAssignParam,
  ReplyFairPriceParam,
  ReviewPriceInfoQueryParam,
  SubmitReviewParam,
  WebResponseListReviewFlowRecordExtDto,
  WebResponseReviewPriceInfoVO,
  WebResponseReviewSupplementInfoVO,
  WebResponseCanWithdrawPriceReviewVO,
  WithdrawPriceReviewParam,
  InquiryBatchTaskVO,
  InquiryBatchTaskItemVO,
  InquiryBatchImportVO,
  WebResponseInquiryBatchTaskVO,
  WebResponseInquiryBatchImportVO,
  WebResponseStandardPageInquiryBatchTaskVO,
  BusinessSubTypeOptionVO,
  BusinessSubTypeEntity,
  BusinessSubTypeUpsertParam,
  StandardPageBusinessSubTypeEntity,
} from '@/types/api';

// ==================== 用户认证相关 ====================

/** 获取当前登录用户详情 GET /api/own */
export async function getCurrentUserDetail(options?: { [key: string]: any }) {
  return request<API.CurrentUserDetailResult>('/own', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 退出认证接口 GET /api/own/logout */
export async function logout(options?: { [key: string]: any }) {
  return request<API.LogoutResult>('/own/logout', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 登录接口 POST /api/auth */
export async function login(body: API.LoginParams, options?: { [key: string]: any }) {
  return request<API.LoginResult>('/auth', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

// ==================== 用户管理相关 ====================

/** 获取用户分页列表 GET /api/users/page */
export async function getUserPage(
  params: {
    page?: number;
    pageSize?: number;
    cnameLike?: string;
    username?: string;
    nicknameLike?: string;
    phone?: string;
    email?: string;
    currentInstitutionId?: number;
    deptId?: number | string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/users/organization/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.UserPageResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 删除用户 DELETE /api/users/{id} */
export async function deleteUser(
  id: number,
  options?: { [key: string]: any },
) {
  return request<API.DeleteResult>(`/users/organization/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 创建用户 POST /api/users */
export async function createUser(
  body: {
    avatar?: string;
    cname: string;
    employeeNumber?: string;
    organizationIds?: (string | number)[];
    deptIds?: (string | number)[];
    description?: string;
    email?: string;
    enabled?: boolean;
    gender?: number;
    nickname?: string;
    password?: string;
    phone?: string;
    roleCodes: string[];
    username: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.CreateUserResponse>('/users/organization', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 获取用户详情 GET /api/users/{id} */
export async function getUserDetail(
  id: string | number,
  options?: { [key: string]: any },
) {
  return request<API.UserDetailResponse>(`/users/organization/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 检查用户名是否存在 GET /api/users/one/by/username */
export async function checkUsernameExists(
  username: string,
  options?: { [key: string]: any },
) {
  return request<API.CheckUsernameResponse>('/users/organization/one/by/username', {
    method: 'GET',
    params: { username },
    ...(options || {}),
  });
}

/** 编辑用户 PUT /api/users/{id} */
export async function updateUser(
  id: string | number,
  body: {
    avatar?: string;
    birthDate?: string;
    cname: string;
    employeeNumber?: string;
    organizationIds: (string | number)[];
    deptIds?: (string | number)[];
    description?: string;
    email?: string;
    enabled?: boolean;
    gender?: number;
    nickname?: string;
    password?: string;
    phone?: string;
    roleCodes: string[];
    username: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.UpdateUserResponse>(`/users/organization/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 批量导入用户 POST /api/users/createExcel */
export async function importUsers(
  formData: FormData,
  options?: { [key: string]: any },
) {
  const result = await request<API.UserImportResponse>('/users/organization/createExcel', {
    method: 'POST',
    data: formData,
    ...(options || {}),
  });

  return result;
}

// ==================== 角色管理相关 ====================

/** 获取角色分页列表 GET /api/roles/page */
export async function getRolePage(
  params: API.RolePageParams,
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/roles/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.RoleListResult>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 新增角色 POST /api/roles */
export async function addRole(
  body: {
    name?: string;
    code?: string;
    description?: string;
    permissions?: string[];
  },
  options?: { [key: string]: any },
) {
  return request<API.RoleListResult>('/roles', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 更新角色 PUT /api/roles/{id} */
export async function updateRole(
  id: number,
  body: {
    name?: string;
    code?: string;
    description?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.UpdateRoleResult>(`/roles/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除角色 DELETE /api/roles/{id} */
export async function removeRole(
  id: number,
  options?: { [key: string]: any },
) {
  return request<API.DeleteResult>(`/roles/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// ==================== 菜单管理相关 ====================

/** 获取菜单树 GET /api/sys/menu/tree */
export async function getMenuTree(options?: { [key: string]: any }) {
  return request<API.MenuTreeResult>('/sys/menu/tree', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 删除菜单 DELETE /api/sys/menu/delete */
export async function deleteMenu(
  id: number,
  options?: { [key: string]: any },
) {
  return request<API.DeleteResult>(`/sys/menu/delete?id=${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 创建或修改菜单 POST /api/sys/menu/save */
export async function saveMenu(
  body: {
    id?: number;
    menuCode?: string;
    menuName?: string;
    parentId?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.MenuSaveResult>('/sys/menu/save', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

// ==================== 权限管理相关 ====================

/** 获取权限分页列表 GET /api/permissions/page */
export async function getPermissionPage(
  params: API.PermissionPageParams,
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/permissions/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.PermissionPageResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建权限 POST /api/permissions */
export async function createPermission(
  body: API.PermissionRequest,
  options?: { [key: string]: any },
) {
  return request<API.PermissionResponse>('/permissions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 编辑权限 PUT /api/permissions/{id} */
export async function updatePermission(
  id: number,
  body: API.PermissionRequest,
  options?: { [key: string]: any },
) {
  return request<API.PermissionResponse>(`/permissions/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除权限 DELETE /api/permissions/{id} */
export async function deletePermission(
  id: number,
  options?: { [key: string]: any },
) {
  return request<API.DeleteResult>(`/permissions/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// ==================== 机构管理相关 ====================

/** 分页查询机构列表 GET /api/admin/organization/page */
export async function getOrganizationPage(
  params: {
    page?: number;
    pageSize?: number;
    organizationName?: string;
    province?: string;
    city?: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/admin/organization/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.OrganizationPageResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 删除机构 DELETE /api/organizations/{id} */
export async function deleteOrganization(
  id: number,
  options?: { [key: string]: any },
) {
  return request<API.DeleteResult>(`/organizations/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 批量删除机构 POST /api/admin/organization/batch/delete */
export async function batchDeleteOrganization(
  body: {
    ids: string[]; // 修改为字符串数组，避免大整数精度问题
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>('/admin/organization/batch/delete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

// ==================== 部门管理相关（机构管理） ====================

/** 查询部门树 GET /api/dept/tree */
export async function getDeptTree(
  options?: { [key: string]: any },
) {
  return request<API.DeptTreeResponse>('/dept/tree', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 查询部门树（根据机构/客户） GET /api/admin/dept/tree */
export async function getDeptTreeByOwner(
  params?: {
    ownerId?: string | number;
    ownerType?: 'ORGANIZATION' | 'CUSTOMER';
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  if (params?.ownerId !== undefined && params?.ownerId !== null) {
    // 直接使用字符串，避免长数字精度丢失
    queryString.append('ownerId', String(params.ownerId));
  }
  if (params?.ownerType) {
    queryString.append('ownerType', params.ownerType);
  }

  const url = `/admin/dept/tree${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.DeptTreeResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 查询分组部门树（按机构-客户类型分组） GET /api/admin/dept/groupedTree */
export async function getDeptGroupedTree(
  options?: { [key: string]: any },
) {
  return request<API.DeptGroupedTreeResponse>('/admin/dept/groupedTree', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 查询行政区划树 GET /api/district/tree */
export async function getDistrictTree(
  params?: {
    parentAdcode?: string;
    maxLevel?: string;
    includeDisabled?: boolean;
  },
  options?: { [key: string]: any },
) {
  return request<API.DistrictTreeResponse>('/district/tree', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 新增或编辑行政区划 POST /api/district */
export async function saveDistrict(
  body: API.DistrictSaveParam,
  options?: { [key: string]: any },
) {
  return request<API.DistrictSaveResponse>('/district', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除行政区划 DELETE /api/district/{id} */
export async function deleteDistrict(
  id: number | string,
  options?: { [key: string]: any },
) {
  return request<API.DistrictDeleteResponse>(`/district/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 删除部门 POST /api/admin/dept/delete */
export async function deleteDept(
  id: string | number,
  options?: { [key: string]: any },
) {
  return request<API.DeptTreeResponse>('/admin/dept/delete', {
    method: 'POST',
    data: {
      id,
    },
    ...(options || {}),
  });
}

/** 保存部门 POST /api/admin/dept/save */
export async function saveDept(
  dept: {
    id?: number;
    name: string;
    parentId?: number;
    ownerId?: number;
    ownerType?: string;
    ownerName?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.DeptSaveResponse>('/admin/dept/save', {
    method: 'POST',
    data: dept,
    ...(options || {}),
  });
}


/** 查询机构详情 GET /api/admin/organization/detail */
export async function getOrganizationDetail(
  params: {
    id: number | string;
  },
  options?: { [key: string]: any },
) {
  return request<API.OrganizationDetailResponse>('/admin/organization/detail', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 新增或编辑机构 POST /api/admin/organization/upsert */
export async function upsertOrganization(
  body: API.OrganizationUpsertParams,
  options?: { [key: string]: any },
) {
  return request<API.OrganizationUpsertResponse>('/admin/organization/upsert', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 导入机构信息 POST /api/admin/organization/import */
export async function importOrganization(
  orgType: string,
  file: File,
  options?: { [key: string]: any },
) {
  const formData = new FormData();
  formData.append('file', file)

  const queryString = new URLSearchParams();
  queryString.append('orgType', orgType);
  return request<API.OrganizationImportResponse>(`/admin/organization/import?${queryString.toString()}`, {
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...(options || {}),
  });
}

// ==================== 客户管理相关 ====================

/** 分页查询客户列表 GET /api/admin/customer/page */
export async function getCustomerPage(
  params: {
    page?: number;
    pageSize?: number;
    customerName?: string;
    customerType?: string;
    status?: number;
    idNumber?: string;
    contactPhone?: string;
    customerContact?: string;
    createTimeStart?: string;
    createTimeEnd?: string;
    updateTimeStart?: string;
    updateTimeEnd?: string;
    province?: string;
    city?: string;
    administrativeDistrict?: string;
    /** 所属渠道（所属银行）名称 */
    bankName?: string;
    organizationIds?: (string | number)[];
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // 特殊处理数组参数 organizationIds
      if (key === 'organizationIds' && Array.isArray(value)) {
        value.forEach((id) => {
          queryString.append('organizationIds', String(id));
        });
      } else {
        queryString.append(key, String(value));
      }
    }
  });

  return request<API.CustomerPageResult>(`/admin/customer/page?${queryString.toString()}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 导入客户信息 POST /api/admin/customer/import */
export async function importCustomer(
  file: File,
  customerType: string,
  options?: { [key: string]: any },
) {
  const formData = new FormData();
  formData.append('file', file);

  const queryString = new URLSearchParams();
  queryString.append('customerType', customerType);

  return request<API.UnifiedResponse>(`/admin/customer/import?${queryString.toString()}`, {
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...(options || {}),
  });
}

/** 批量添加客户用户 POST /api/users/customer/createExcel */
export async function importCustomerUser(
  file: File,
  options?: { [key: string]: any },
) {
  const formData = new FormData();
  formData.append('file', file);

  return request<API.UnifiedResponse>('/users/customer/createExcel', {
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...(options || {}),
  });
}

/** 获取客户用户分页列表 GET /api/users/customer/page */
export async function getCustomerUserPage(
  params: {
    /** 不传则跨客户按其他条件查询 */
    customerId?: string;
    page?: number;
    pageSize?: number;
    cnameLike?: string;
    username?: string;
    nicknameLike?: string;
    phone?: string;
    email?: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });
  return request<API.WebResponseStandardPageCustomerUserVO>(`/users/customer/page?${queryString.toString()}`, {
    method: 'GET',
    ...(options || {}),
  });
}

// ==================== 操作日志相关 ====================

/** 获取日志分页列表 GET /api/logs/page */
export async function getLogPage(
  params: {
    page: number;
    pageSize: number;
    username?: string;
    operation?: string;
    method?: string;
    startTime?: string;
    endTime?: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/logs/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.LogPageResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 删除日志 DELETE /api/logs/{id} */
export async function deleteLog(
  id: number,
  options?: { [key: string]: any },
) {
  return request<API.DeleteResult>(`/logs/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// ==================== API管理相关 ====================

/** 获取API分页列表 GET /api/apis/page */
export async function getApiPage(
  params: {
    appId: number;
    page?: number;
    pageSize?: number;
    authType?: number;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/apis/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.ApiPageResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

// ==================== 角色权限管理相关 ====================

/** 关联角色菜单 POST /api/sys/menu/role/association */
export async function associateRoleMenus(
  params: {
    menuCodes: string[];
    roleCode: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse>('/sys/menu/role/association', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: params,
    ...(options || {}),
  });
}

/** 覆盖指定角色权限 PUT /api/roles/{roleCode}/permissions */
export async function updateRolePermissions(
  roleCode: string,
  params: {
    permissionCodes: string[];
  },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse>(`/roles/${roleCode}/permissions`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: params,
    ...(options || {}),
  });
}

// ==================== 权限API管理相关 ====================

/** 获取指定权限的API列表 GET /api/permissions/{permissionCode}/apis */
export async function getPermissionApis(
  permissionCode: string,
  options?: { [key: string]: any },
) {
  return request<API.PermissionApiListResponse>(`/permissions/${permissionCode}/apis`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 更新指定权限的API PUT /api/permissions/{permissionCode}/apis */
export async function updatePermissionApis(
  permissionCode: string,
  params: {
    apiCodes: string[];
  },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse>(`/permissions/${permissionCode}/apis`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: params,
    ...(options || {}),
  });
}

// ==================== 文件管理相关 ====================

/** 上传文件 POST /api/files */
export async function uploadFile(
  formData: FormData,
  options?: { [key: string]: any },
) {
  const result = await request<API.FileUploadResponse>('/files', {
    method: 'POST',
    data: formData,
    ...(options || {}),
  });

  return result;
}

/** 预上传文件（TEMP）POST /api/files/upload */
export async function uploadTempFile(
  formData: FormData,
  options?: { [key: string]: any },
) {
  return request<API.FileUploadResponse>('/files/upload', {
    method: 'POST',
    data: formData,
    ...(options || {}),
  });
}

/** 下载文件 GET /api/files/{id} */
export async function downloadFile(
  id: number | string,
  params?: {
    width?: number;
    height?: number;
    total?: boolean;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryString.append(key, String(value));
      }
    });
  }

  const url = `/files/${id}${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<Blob>(url, {
    method: 'GET',
    responseType: 'blob',
    ...(options || {}),
  });
}

/** 文件持久化 POST /api/files/{id}/persist */
export async function persistFile(
  id: number,
  params?: {
    level?: number;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryString.append(key, String(value));
      }
    });
  }

  const url = `/files/${id}/persist${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  const result = await request<API.UnifiedResponse>(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    ...(options || {}),
  });

  return result;
}

// ==================== 行政区划相关 ====================

/** 获取所有省份 GET /api/district/provinces */
export async function getProvinces(
  options?: { [key: string]: any },
) {
  return request<API.ProvinceListResponse>('/district/provinces', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 根据省份编码获取所有城市 GET /api/district/cities */
export async function getCitiesByProvince(
  params: {
    provinceAdcode: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/district/cities${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.CityListResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 根据城市编码获取所有区县 GET /api/district/districts */
export async function getDistrictsByCity(
  params: {
    cityAdcode: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/district/districts${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.CountyListResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 根据区县编码获取所有街道 GET /api/district/streets */
export async function getStreetsByDistrict(
  params: {
    districtAdcode: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/district/streets${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.CountyListResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 切换客户状态 POST /api/admin/customer/toggle/status */
export async function toggleCustomerStatus(
  params: {
    id: string | number;
  },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse>('/admin/customer/toggle/status', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 删除客户 POST /api/admin/customer/delete */
export async function deleteCustomer(
  params: {
    id: string | number;
  },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse>('/admin/customer/delete', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 批量编辑客户报告总价展示精度 POST /api/admin/customer/amountPrecision/batch */
export async function batchUpdateCustomerAmountPrecision(
  body: {
    items: Array<{
      customerId: string | number;
      totalAmountPrecision?: number | null;
    }>;
  },
  options?: { [key: string]: any },
) {
  return request<API.CustomerAmountPrecisionBatchResponse>('/admin/customer/amountPrecision/batch', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 批量编辑客户配置参数 POST /api/admin/customer/config/batch */
export async function batchUpdateCustomerConfig(
  body: {
    items: Array<{
      customerId: string | number;
      totalAmountPrecisionSpecified?: boolean;
      secondReviewTimeSpecified?: boolean;
      secondReviewMinMinutes?: number | null;
      secondReviewMaxRandomMinutes?: number | null;
      defaultAuditorSecondReviewSpecified?: boolean;
      defaultAuditorSecondReview?: string | number | null;
      thirdReviewTimeSpecified?: boolean;
      thirdReviewMinMinutes?: number | null;
      thirdReviewMaxRandomMinutes?: number | null;
      defaultAuditorThirdReviewSpecified?: boolean;
      defaultAuditorThirdReview?: string | number | null;
      sealTimeSpecified?: boolean;
      sealMinMinutes?: number | null;
      sealMaxRandomMinutes?: number | null;
      defaultAuditorSealSpecified?: boolean;
      defaultAuditorSeal?: string | number | null;
      totalAmountPrecision?: number | null;
      secondReviewEnabled?: boolean | null;
      thirdReviewEnabled?: boolean | null;
      sealEnabled?: boolean | null;
      /** 测算开关：是否开启正向测算 */
      forwardCalculationEnabled?: boolean | null;
      /** 测算开关：是否开启因子逆推 */
      factorReverseEnabled?: boolean | null;
    }>;
  },
  options?: { [key: string]: any },
) {
  return request<API.CustomerAmountPrecisionBatchResponse>('/admin/customer/config/batch', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/**
 * 创建客户配置 Excel 异步导入任务
 * POST /api/admin/customer/config-import/tasks
 * FormData: file + type（AUTO_AUDIT_CONFIG | AUTO_AUDIT_RULE | VALUATION_EXECUTION_CONFIG | VALUATION_EXECUTION_RULE | OUR_CONTACT_RULE）
 */
export async function createCustomerConfigImportTask(
  file: File,
  type: string,
  options?: { [key: string]: any },
) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  return request<API.UnifiedResponse<API.CustomerConfigImportTaskVO>>(
    '/admin/customer/config-import/tasks',
    {
      method: 'POST',
      data: formData,
      ...(options || {}),
    },
  );
}

/** 查询客户配置 Excel 导入任务 GET /api/admin/customer/config-import/tasks/{taskId} */
export async function getCustomerConfigImportTask(
  taskId: string,
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<API.CustomerConfigImportTaskVO>>(
    `/admin/customer/config-import/tasks/${taskId}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/** 下载客户配置导入失败明细 GET /api/admin/customer/config-import/tasks/{taskId}/failures/download */
export async function downloadCustomerConfigImportFailures(
  taskId: string,
  options?: { [key: string]: any },
) {
  return request<Blob>(
    `/admin/customer/config-import/tasks/${taskId}/failures/download`,
    {
      method: 'GET',
      responseType: 'blob',
      ...(options || {}),
    },
  );
}

/** 客户配置 Excel 导入任务分页 POST /api/admin/customer/config-import/tasks/list */
export async function listCustomerConfigImportTasks(
  body: {
    page?: number;
    pageSize?: number;
    type?: string;
    status?: string;
    createTimeStart?: number;
    createTimeEnd?: number;
  },
  options?: { [key: string]: any },
) {
  return request<
    API.UnifiedResponse<{
      page?: number;
      pageSize?: number;
      totalPage?: number;
      total?: number;
      list?: API.CustomerConfigImportTaskVO[];
    }>
  >('/admin/customer/config-import/tasks/list', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 下载系统文件 GET /api/admin/system/download */
export async function downloadSystemFile(
  params: {
    fileTypeCode: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/admin/system/download${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<Blob>(url, {
    method: 'GET',
    responseType: 'blob',
    ...(options || {}),
  });
}

/** 新增或编辑客户 POST /api/admin/customer/upsert */
export async function upsertCustomer(
  data: any,
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse>('/admin/customer/upsert', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

/** 查询客户详情 GET /api/admin/customer/detail */
export async function getCustomerDetail(
  params: {
    id: string | number;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/admin/customer/detail${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.UnifiedResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 搜索客户信息 GET /api/admin/customer/getCustomerFull */
export async function getCustomerFull(
  params: {
    taskId: number | string;
    page?: number;
    pageSize?: number;
    customerName?: string;
    customerType?: string;
    province?: string;
    city?: string;
    administrativeDistrict?: string;
    status?: number;
    idNumber?: string;
    contactPhone?: string;
    customerContact?: string;
    createTimeStart?: string;
    createTimeEnd?: string;
    updateTimeStart?: string;
    updateTimeEnd?: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();

  // 必需参数
  queryString.append('taskId', params.taskId.toString());

  // 可选参数
  if (params.page !== undefined) {
    queryString.append('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    queryString.append('pageSize', String(params.pageSize));
  }
  if (params.customerName) {
    queryString.append('customerName', params.customerName);
  }
  if (params.customerType) {
    queryString.append('customerType', params.customerType);
  }
  if (params.province) {
    queryString.append('province', params.province);
  }
  if (params.city) {
    queryString.append('city', params.city);
  }
  if (params.administrativeDistrict) {
    queryString.append('administrativeDistrict', params.administrativeDistrict);
  }
  if (params.status !== undefined) {
    queryString.append('status', String(params.status));
  }
  if (params.idNumber) {
    queryString.append('idNumber', params.idNumber);
  }
  if (params.contactPhone) {
    queryString.append('contactPhone', params.contactPhone);
  }
  if (params.customerContact) {
    queryString.append('customerContact', params.customerContact);
  }
  if (params.createTimeStart) {
    queryString.append('createTimeStart', params.createTimeStart);
  }
  if (params.createTimeEnd) {
    queryString.append('createTimeEnd', params.createTimeEnd);
  }
  if (params.updateTimeStart) {
    queryString.append('updateTimeStart', params.updateTimeStart);
  }
  if (params.updateTimeEnd) {
    queryString.append('updateTimeEnd', params.updateTimeEnd);
  }

  return request<API.CustomerFullSearchResult>(`/admin/customer/getCustomerFull?${queryString.toString()}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 禁用用户 PUT /api/users/{id}/disable */
export async function disableUser(
  id: number,
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse>(`/users/organization/${id}/disable`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    ...(options || {}),
  });
}

/** 启用用户 PUT /api/users/{id}/enable */
export async function enableUser(
  id: number,
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse>(`/users/organization/${id}/enable`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    ...(options || {}),
  });
}

/** 获取行政区划的完整名称 GET /api/district/fullname/{adcode} */
export async function getDistrictFullname(
  adcode: string,
  options?: { [key: string]: any },
) {
  return request<string>(`/district/fullname/${adcode}`, {
    method: 'GET',
    ...(options || {}),
  });
}

// ==================== 个人设置相关 ====================

/** 修改个人设置 PUT /api/users/modify/{id} */
export async function modifyPersonalSettings(
  id: string | number,
  body: {
    avatar?: string;
    birthDate?: string;
    cname?: string;
    organizationIds?: number[];
    description?: string;
    email?: string;
    enabled?: boolean;
    gender?: number;
    nickname?: string;
    password?: string;
    phone?: string;
    roleCodes?: string[];
    username?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.UpdateUserResponse>(`/users/modify/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 用户修改密码 PUT /api/users/comparePassword */
export async function changeUserPassword(
  body: {
    newPassword: string;
    newPasswordIv: string;
    oldPassword: string;
    oldPasswordIv: string;
    userId: number;
    username?: string;
  },
  options?: { [key: string]: any }
) {
  return request<any>('/users/comparePassword', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 重置用户密码（不需要旧密码） PUT /api/users/resetPassword */
export async function resetUserPassword(
  body: {
    userId: number;
    newPassword: string;
  },
  options?: { [key: string]: any }
) {
  return request<API.UnifiedResponse>('/users/resetPassword', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

// ==================== 操作日志相关 ====================

/** 分页查询操作日志 POST /api/log/page */
export async function getOperationLogPage(
  body: any,
  options?: { [key: string]: any }
) {
  return request<any>('/log/page', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 获取操作模块列表 GET /apis/descriptions */
export async function getOperationModules(
  options?: { [key: string]: any }
) {
  return request<any>('/apis/descriptions', {
    method: 'GET',
    ...(options || {}),
  });
}

// ==================== 智能识别相关 ====================

/** 身份证识别 POST /api/ocr/id-card */
export async function recognizeIdCard(
  body: { fileIds: string[] },
  options?: { [key: string]: any }
) {
  return request<API.IdCardRecognitionResult>('/ocr/id-card', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 营业执照识别 POST /api/ocr/business-license */
export async function recognizeBusinessLicense(
  body: { fileIds: string[] },
  options?: { [key: string]: any }
) {
  return request<API.BusinessLicenseRecognitionResult>('/ocr/business-license', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 权属证明解析 POST /api/admin/ownership/ocr/analysis */
export async function analyzeOwnershipCertificate(
  body: { fileIds: number[] },
  options?: { [key: string]: any }
) {
  return request<API.WebResponseOwnershipInfoVO>('/admin/ownership/ocr/analysis', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

// ==================== 任务管理相关 ====================

/** 获取任务列表 POST /api/admin/task/list */
export async function getTaskList(
  body: {
    businessLines?: number;
    businessType?: number;
    businessSubTypeId?: number | string;
    businessSubTypeName?: string;
    city?: string;
    creator?: string;
    district?: string;
    electronicReportCode?: string;
    endTime?: number;
    estimateReportCode?: string;
    page?: number;
    pageSize?: number;
    province?: string;
    startTime?: number;
    /** 我的任务分类（MyTaskStatus：PENDING_HANDLE / PROCESSED），不传或空为全部 */
    status?: string;
    /**
     * 列表展示态精确筛选（TaskAssignDisplayStatus）。
     * 不传 / null / 空串不限制；非法编码由后端拒绝。勿传 ALL。
     */
    flowDisplayStatus?: string;
    statusCategory?: number;
    taskId?: number | string;
    taskName?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.TaskListResponse>('/admin/task/list', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 获取任务列表（不按登录人过滤）POST /api/admin/task/list/v2 */
export async function getTaskListV2(
  body: API.TaskAdminPageParam,
  options?: { [key: string]: any },
) {
  return request<any>('/admin/task/list/v2', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 查询草稿任务列表（根据当前登录用户过滤）POST /api/admin/taskDraft/list */
export async function getTaskDraftList(
  body: {
    taskId?: number;
    taskName?: string;
    statusCategory?: number;
    businessLines?: number;
    businessType?: number;
    creator?: string;
    province?: string;
    provinceCode?: string;
    city?: string;
    cityCode?: string;
    district?: string;
    districtCode?: string;
    electronicReportCode?: string;
    estimateReportCode?: string;
    startTime?: number;
    endTime?: number;
    page?: number;
    pageSize?: number;
    businessSource?: string;
    customerId?: number;
    customerName?: string;
    customerManager?: string;
    organizationIds?: number[];
    startTimeLocalDateTime?: string;
    endTimeLocalDateTime?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.TaskListResponse>('/admin/taskDraft/list', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 查询接单任务列表（营销经理查看待分派和已分派的任务列表）GET /api/admin/customerTask/task/assignList */
export async function getAssignTaskList(
  params: {
    pageNum?: number;
    pageSize?: number;
    status?: string;
    customerName?: string;
    taskName?: string;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success?: boolean;
    message?: string;
    result?: {
      page?: number;
      pageSize?: number;
      totalPage?: number;
      total?: number;
      list?: any[];
      empty?: boolean;
    };
    [key: string]: any;
  }>('/admin/customerTask/task/assignList', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 对客户任务进行分派/释放 POST /api/admin/customerTask/task/operate */
export async function operateCustomerTask(
  body: {
    customerTaskId: number | string;
    operateType: 'ASSIGN' | 'RELEASE';
    operatorUserId?: number | string;
    operatorUserName?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseString>('/admin/customerTask/task/operate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 获取客户任务详情 GET /api/admin/customerTask/customerTask/detail/{customerTaskId} */
export async function getCustomerTaskDetail(
  customerTaskId: number | string,
  options?: { [key: string]: any },
) {
  return request<{
    success?: boolean;
    message?: string;
    result?: any;
    [key: string]: any;
  }>(`/admin/customerTask/customerTask/detail/${customerTaskId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 发起-编辑客户任务 POST /api/admin/customerTask/inquiryInitiateTask */
export async function inquiryInitiateTask(
  body: {
    provinceCode?: string;
    cityCode?: string;
    districtCode?: string;
    owner?: string;
    cardAddress?: string;
    location?: string;
    buildingArea?: number;
    innerArea?: number;
    sharedLandArea?: number;
    landArea?: number;
    landExpiry?: number;
    province?: string;
    city?: string;
    district?: string;
    building?: string;
    buildingNo?: string;
    unitNo?: string;
    householdNo?: string;
    certificateOfTitleNo?: string;
    cardUsage?: string;
    landUsage?: string;
    houseUsage?: string;
    actualUsedType?: string;
    finishTime?: string;
    bedroom?: number;
    hall?: number;
    kitchen?: number;
    toilet?: number;
    shopsType?: string;
    shopsAmount?: number;
    x?: number;
    y?: number;
    propertyCert?: string;
    totalFloors?: string;
    floorPosition?: string;
    coOwnership?: string;
    coOwnershipCircumstance?: string;
    rightType?: string;
    rightNature?: string;
    purchaseDuration?: string;
    taxPolicy?: string;
    structure?: string;
    sharedBuildingArea?: number;
    houseRightType?: string;
    landRightType?: string;
    landRightNature?: string;
    houseRightNature?: string;
    surveyingAndMappingUnit?: string;
    undergroundFloors?: string;
    onTheGroundFloors?: string;
    parcelNumber?: string;
    propertyUnitNumber?: string;
    individualLandArea?: string;
    registrationTime?: string;
    landCert?: string;
    propertyCertList?: string;
    street?: string;
    streetCode?: string;
    inquiryId?: string;
    businessLines?: number;
    businessType?: number;
    businessSubTypeId?: number | string | null;
    businessSource?: string;
    usageStatus?: string;
    inquiryPerson?: string;
    remarks?: string;
    ownershipFileIds?: string[];
    letterEntrustmentFileIds?: string[];
    customerTaskId?: string;
    taskId?: string;
    evaluationFileIds?: string[];
    businessLicenseFiles?: string[];
    ownershipType?: string;
    taskType?: string;
    specialSituation?: string | null;
    marketingManagerContactMethod?: string;
    marketingManagerName?: string;
    debtor?: string;
    entrustorId?: string;
    customerId?: string;
    customerName?: string;
    landExpiryLocalDateTime?: string;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success?: boolean;
    message?: string;
    result?: {
      taskId?: string;
      customerTaskId?: string;
    };
    [key: string]: any;
  }>('/admin/customerTask/inquiryInitiateTask', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 查询估价草稿详情（按来源区分）POST /api/admin/taskDraft/getOrCreate */
export async function getOrCreateTaskDraft(
  body: {
    sourceType: string;
    sourceId: string;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success?: boolean;
    message?: string;
    result?: {
      draftId?: number | string;
      sourceType?: string;
      sourceId?: number | string;
      ownershipInfo?: any;
      ownershipDraft?: any;
      propertyCondition?: any;
      propertyDraft?: any;
      recordDraft?: any;
      imgInfoList?: Array<{
        id?: string;
        fileName?: string;
        type?: number;
        sequence?: number;
      }>;
      status?: string;
    };
    [key: string]: any;
  }>('/admin/taskDraft/getOrCreate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 覆盖写入草稿明细数据 POST /api/admin/taskDraft/upsert */
export async function upsertTaskDraft(
  body: {
    draftId: number | string;
    sourceType: string;
    sourceId: number | string;
    recordDraft?: {
      id?: number;
      taskName?: string;
      businessLines?: number;
      businessType?: number;
      businessSubTypeId?: number | string | null;
      customerId?: number;
      customerName?: string;
      customerManager?: string;
      debtor?: string;
      remarks?: string;
      currentOperator?: number;
      customerTaskId?: number;
      taskSource?: string;
      businessSource?: string;
      taskType?: string;
      specialSituation?: string | null;
      specialSituationName?: string | null;
    };
    ownershipDraft?: {
      id?: number;
      owner?: string;
      cardAddress?: string;
      province?: string;
      provinceCode?: string;
      city?: string;
      cityCode?: string;
      district?: string;
      districtCode?: string;
      street?: string;
      streetCode?: string;
      building?: string;
      buildingNo?: string;
      unitNo?: string;
      householdNo?: string;
      bedroom?: number;
      hall?: number;
      kitchen?: number;
      toilet?: number;
      buildingArea?: number;
      x?: number;
      y?: number;
      finishTime?: string;
      certificateOfTitleNo?: string;
      location?: string;
      cardUsage?: string;
      landUsage?: string;
      houseUsage?: string;
      actualUsedType?: string;
      propertyCert?: string;
      innerArea?: number;
      sharedLandArea?: number;
      sharedBuildingArea?: number;
      totalFloors?: string;
      floorPosition?: string;
      landArea?: number;
      landExpiry?: number;
      coOwnership?: string;
      coOwnershipCircumstance?: string;
      rightType?: string;
      houseRightType?: string;
      landRightType?: string;
      rightNature?: string;
      landRightNature?: string;
      houseRightNature?: string;
      usageStatus?: string;
      purchaseDuration?: string;
      ownerNature?: string;
      taxPolicy?: string;
      structure?: string;
      surveyingAndMappingUnit?: string;
      undergroundFloors?: string;
      onTheGroundFloors?: string;
      parcelNumber?: string;
      propertyUnitNumber?: string;
      individualLandArea?: string;
      shopsType?: string;
      shopsAmount?: number;
      registeredPrice?: number;
      certSourceType?: string;
      registrationTime?: number;
      landCert?: string;
      propertyCertList?: string;
    };
    propertyDraft?: {
      id?: number;
      location?: string;
      eastBoundary?: string;
      southBoundary?: string;
      westBoundary?: string;
      northBoundary?: string;
      orientation?: string;
      renovation?: string;
      layout?: string;
      facilities?: string;
      lightingVentilation?: string;
      lightingCondition?: string;
      ventilationCondition?: string;
      landShape?: string;
      landTopography?: string;
      infrastructure?: string;
    };
    imgInfoList?: Array<{
      id?: string;
      fileName?: string;
      type?: number;
      sequence?: number;
    }>;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success?: boolean;
    message?: string;
    result?: boolean;
    [key: string]: any;
  }>('/admin/taskDraft/upsert', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 查询所有待接单状态的客户任务列表 GET /api/admin/customerTask/task/pendingList */
export async function getCustomerTaskPendingList(
  params: {
    pageNum?: number;
    pageSize?: number;
    taskName?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.CustomerTaskPendingListResponse>('/admin/customerTask/task/pendingList', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 权属证明信息及文件编辑&保存接口 POST /api/admin/ownership/saveInfoAndFile */
export async function saveOwnershipInfoAndFile(
  body: API.OwnershipSaveParams,
  options?: { [key: string]: any },
) {
  return request<API.OwnershipSaveResponse>('/admin/ownership/saveInfoAndFile', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除任务接口 GET /api/admin/task/delete */
export async function deleteTask(
  params: {
    taskId: string | number;
  },
  options?: { [key: string]: any },
) {
  return request<API.DeleteTaskResponse>('/admin/task/delete', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 权属证明查询管理端接口 GET /api/admin/ownership/getByTaskId */
export async function getOwnershipByTaskId(
  params: {
    taskId: string | number;
  },
  options?: { [key: string]: any },
) {
  return request<API.OwnershipInfoResponse>('/admin/ownership/getByTaskId', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 管理端房地产实物状况信息查询接口 GET /api/admin/propertyCondition/getByTaskId */
export async function getPropertyConditionByTaskId(
  params: {
    taskId: string | number;
  },
  options?: { [key: string]: any },
) {
  return request<API.PropertyConditionResponse>('/admin/propertyCondition/getByTaskId', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 管理端房地产实物状况信息保存接口 POST /api/admin/propertyCondition/info/save */
export async function savePropertyConditionInfo(
  body: API.PropertyConditionAdminParam,
  options?: { [key: string]: any },
) {
  return request<API.PropertyConditionSaveResponse>('/admin/propertyCondition/info/save', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 实勘图图像分析接口 POST /api/admin/file/surveyMaps/analysis */
export async function analyzeSurveyMaps(
  body: {
    fileIds?: number[];
    taskId?: number | string;
  },
  options?: { [key: string]: any },
) {
  return request<{
    detailMessage?: any[];
    httpStatusCode?: number;
    logLevel?: string;
    message?: string;
    remarks?: string;
    result?: API.PropertyConditionVO;
    serverSysTime?: number;
    statusCode?: string;
    success?: boolean;
    version?: string;
  }>('/admin/file/surveyMaps/analysis', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/**
 * 获取比价资料
 * @param taskId 任务ID
 * @param options 请求选项
 * @returns 比价资料响应
 */
export async function getComparativePrices(
  taskId: number | string,
  options?: { propertyId?: number | string; [key: string]: any },
) {
  const { propertyId, ...rest } = options || {};
  return request<API.WebResponseComparativePricesVO>('/admin/surroundings/del/comparativePrices', {
    method: 'GET',
    params: {
      taskId,
      ...(propertyId != null && String(propertyId).trim() !== ''
        ? { propertyId: String(propertyId) }
        : {}),
    },
    ...rest,
  });
}

/**
 * 询价/估价历史记录分页
 * 后端接口：/api/ccb-ai/inquiry/valuationHistory
 */
export async function getInquiryValuationHistory(
  params: import('@/types/api').InquiryValuationHistoryQueryParams = {},
  options?: { [key: string]: any },
) {
  return request<import('@/types/api').WebResponseInquiryValuationHistoryPageVO>(
    '/ccb-ai/inquiry/valuationHistory',
    {
      method: 'GET',
      params: {
        taskSource: params.taskSource || undefined,
        startTime: params.startTime,
        endTime: params.endTime,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 5,
        actualUsedType: params.actualUsedType || undefined,
        customerManager: params.customerManager || undefined,
        keyWord: params.keyWord || undefined,
      },
      ...(options || {}),
    },
  );
}

/**
 * 获取同区域成交信息（已取消分页，返回所有数据）
 * @param params 查询参数
 * @param options 请求选项
 * @returns 同区域成交信息响应
 */
export async function getSameAreaTransactionInfo(
  params: {
    taskId: number | string;
    /** 多套逐套查询必填 */
    propertyId?: number | string;
    communityName?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseListCommunityTransactionInfo>('/admin/surroundings/del/sameAreaInfo', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/**
 * 获取同区域挂牌信息
 * @param params 查询参数
 * @param options 请求选项
 * @returns 同区域挂牌信息响应
 */
export async function getSameAreaListingInfo(
  params: {
    taskId: number | string;
    /** 多套逐套查询必填 */
    propertyId?: number | string;
    communityName?: string;
    limit?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseListCommunityTransactionInfo>('/admin/surroundings/del/sameAreaListingInfo', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/**
 * 获取估价结果详情
 * @param body 查询参数
 * @param options 请求选项
 * @returns 估价结果详情响应
 */
export async function getValuationResultDetail(
  body: API.ValuationResultQueryParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseValuationResultDetailVO>('/admin/valuationResultEntity/detail', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/**
 * 获取因子模版列表
 * @param options 请求选项
 * @returns 因子模版列表响应
 */
export async function getFactorTemplateList(
  options?: { [key: string]: any },
) {
  return request<API.WebResponseStandardListFactorTemplateEntity>('/admin/factorTemplateEntity/list', {
    method: 'GET',
    ...(options || {}),
  });
}

/**
 * 获取因子模版详情
 * @param templateId 模版ID
 * @param options 请求选项
 * @returns 因子模版详情响应
 */
export async function getFactorTemplateDetail(
  templateId: number,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseFactorTemplateDetailVO>('/admin/factorTemplateEntity/detail', {
    method: 'GET',
    params: {
      templateId,
    },
    ...(options || {}),
  });
}

/**
 * 创建任务（生成taskId）
 * @param params 创建任务参数
 * @param params.taskName 任务名（可选）
 * @param options 请求选项
 * @returns 创建结果响应，包含taskId
 */
export async function createTask(
  params?: {
    taskName?: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  if (params?.taskName) {
    queryString.append('taskName', params.taskName);
  }

  const url = `/admin/task/create${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<{
    success?: boolean;
    message?: string;
    result?: number; // taskId (integer(int64))
    [key: string]: any;
  }>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 线上取号，创建正式任务。POST /ccb-ai/task/onlineTakeNumber/create */
export async function createOnlineTakeNumber(
  data: {
    businessLines: number;
    businessType: number;
    businessSubTypeId?: number | string;
    provinceCode?: string;
    cityCode?: string;
    districtCode?: string;
    landRightNature?: string;
    actualUsedType?: string;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success?: boolean;
    message?: string;
    result?: number | string;
  }>('/ccb-ai/task/onlineTakeNumber/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

/**
 * 编辑任务
 * @param params 任务记录编辑参数
 * @param options 请求选项
 * @returns 编辑结果响应
 */
export async function editTask(
  params: API.TaskRecordEditParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseBoolean>('/admin/task/edit', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 与小程序提交审核同一接口：POST /ccb-ai/task/edit */
export async function editCcbTask(
  params: API.TaskRecordEditParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseBoolean>('/ccb-ai/task/edit', {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
    ...(options || {}),
  });
}

/**
 * 创建任务报告流程（指定审核人，包含机构信息）
 * @param params 创建流程参数
 * @param options 请求选项
 * @returns 创建结果响应
 */
export async function createFlowWithAssignUsersWithOrg(
  params: API.CreateFlowWithAssignUsersWithOrgParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseBoolean>('/admin/taskReportFlow/createFlowWithAssignUsersWithOrg', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/**
 * 创建流程-重新提交（指定审核人，包含机构信息）
 * 后端接口：/api/ccb-ai/task/createFlowWithAssignUsersWithOrg
 */
export async function createFlowWithAssignUsersWithOrgCcbAi(
  params: any,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseFlowDto>('/ccb-ai/task/createFlowWithAssignUsersWithOrg', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/**
 * 是否可撤回价格复核
 * 后端接口：/api/ccb-ai/task/canWithdrawPriceReview
 */
export async function canWithdrawPriceReview(
  params: {
    taskId: string | number;
  },
  options?: { [key: string]: any },
) {
  return request<WebResponseCanWithdrawPriceReviewVO>(
    '/ccb-ai/task/canWithdrawPriceReview',
    {
      method: 'GET',
      params: {
        taskId: params.taskId,
      },
      ...(options || {}),
    },
  );
}

/**
 * 撤回价格复核
 * 后端接口：/api/ccb-ai/task/withdrawPriceReview
 */
export async function withdrawPriceReview(
  params: WithdrawPriceReviewParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseBoolean>('/ccb-ai/task/withdrawPriceReview', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/**
 * 价格调整或回价
 * 后端接口：/api/ccb-ai/review/flow/adjustPriceOrReply
 */
export async function adjustPriceOrReply(
  params: {
    reviewId: number | string;
    mainHouse?: {
      area?: number;
      fairUnitPrice?: number;
      fairTotalPrice?: number;
      reviewUnitPrice?: number;
      reviewTotalPrice?: number;
    };
    ancillaryFacilities?: Array<{
      id?: number | string;
      name?: string;
      facilityType?: string;
      area?: number;
      unitPrice?: number;
      totalPrice?: number;
    }>;
    totalPrice?: number;
    unitPrice?: number;
    fairPrice?: number;
    fairUnitPrice?: number;
    adjustReason?: string;
    assignUserDto?: { userIds?: string[] };
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseString>('/ccb-ai/review/flow/adjustPriceOrReply', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/**
 * 绑定复核评估材料（全量替换）
 * POST /api/ccb-ai/review/msg/attachment/bind
 * fileType=20 为评估材料；空列表暂不支持，无材料时不要调用
 */
export async function bindReviewAttachment(
  body: {
    reviewId: number | string;
    fileType: number;
    fileIds: Array<number | string>;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseBoolean>('/ccb-ai/review/msg/attachment/bind', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/**
 * 根据复核记录id查询复核补充信息详情（含标的物-附属价格明细）
 * 后端接口：/api/ccb-ai/review/msg/detailByInquiryId
 */
export async function getReviewSupplementInfoByInquiryId(
  params: {
    /** 复核记录ID */
    id: string | number;
    /** 询价ID（当前页面需求不传，但接口支持） */
    inquiryId?: string | number;
  },
  options?: { [key: string]: any },
) {
  const query: { [key: string]: string } = {
    id: String(params.id),
  };
  if (params.inquiryId !== undefined && params.inquiryId !== null && params.inquiryId !== '') {
    query.inquiryId = String(params.inquiryId);
  }

  return request<WebResponseReviewSupplementInfoVO>('/ccb-ai/review/msg/detailByInquiryId', {
    method: 'GET',
    params: query,
    ...(options || {}),
  });
}

/**
 * 查询询价复核价格信息（taskId / customerTaskId / inquiryId 三选一）
 * 后端接口：/api/ccb-ai/review/msg/price/info
 */
export async function getReviewPriceInfo(
  params: ReviewPriceInfoQueryParam,
  options?: { [key: string]: any },
) {
  const query = new URLSearchParams();
  if (params.taskId !== undefined && params.taskId !== null && String(params.taskId).trim() !== '') {
    query.append('taskId', String(params.taskId));
  }
  if (params.customerTaskId !== undefined && params.customerTaskId !== null && String(params.customerTaskId).trim() !== '') {
    query.append('customerTaskId', String(params.customerTaskId));
  }
  if (params.inquiryId !== undefined && params.inquiryId !== null && String(params.inquiryId).trim() !== '') {
    query.append('inquiryId', String(params.inquiryId));
  }
  return request<WebResponseReviewPriceInfoVO>(`/ccb-ai/review/msg/price/info?${query.toString()}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/**
 * 编辑/暂存复核资料
 * 后端接口：/api/ccb-ai/review/msg/edit
 */
export async function editReview(
  body: SubmitReviewParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseString>('/ccb-ai/review/msg/edit', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/**
 * 公允价回复
 * 后端接口：/api/ccb-ai/review/flow/replyFairPrice
 */
export async function replyFairPrice(
  body: ReplyFairPriceParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseBoolean>('/ccb-ai/review/flow/replyFairPrice', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/**
 * 办理复核流程（支持指定下一节点审核人）
 * 后端接口：/api/ccb-ai/review/flow/processFlowWithComment
 */
export async function processReviewFlowWithComment(
  body: ProcessReviewFlowWithAssignParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>('/ccb-ai/review/flow/processFlowWithComment', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/** 多套逐套保存公允价 / 复核价，不推进整任务流程 */
export async function saveReviewPropertyPrice(
  body: {
    reviewId: number | string;
    propertyId: string;
    stage: 'FAIR_PRICE' | 'REVIEW_PRICE';
    expectedVersion: string;
    expectedValuationVersion: string;
    priceDetail?: {
      mainHouse?: {
        id?: string;
        fairUnitPrice?: number;
        fairTotalPrice?: number;
        reviewUnitPrice?: number;
        reviewTotalPrice?: number;
      };
      ancillaryItems?: Array<{
        id?: string;
        fairUnitPrice?: number;
        fairTotalPrice?: number;
        reviewUnitPrice?: number;
        reviewTotalPrice?: number;
      }>;
    };
    totalPrice?: number;
    unitPrice?: number;
    reviewInfo?: {
      landArea?: number;
      valuationObjectType?: string;
    };
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>('/ccb-ai/review/flow/propertyPrice/save', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/**
 * 确认复核
 * 后端接口：/api/ccb-ai/review/flow/confirm
 */
export async function confirmReview(
  body: ConfirmReviewParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseString>('/ccb-ai/review/flow/confirm', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/** 发起复核参数 POST /api/ccb-ai/review/flow/reviewSubmit */
export type ReviewFlowReviewSubmitParam = {
  dataSourceId?: number | string;
  dataSource?: 'INQUIRY' | 'VALUATION_TASK' | 'CUSTOMER_TASK' | string;
  reviewId?: number;
  buildingArea?: number;
  inquirerName?: string;
  contactPhone?: string;
  disputeReason?: string;
  hall?: number;
  bedroom?: number;
  kitchen?: number;
  toilet?: number;
  expectedAmount?: number;
  remark?: string;
  certificateFileIds?: number[];
  houseFileIds?: number[];
  otherProofFileIds?: number[];
};

/**
 * 发起复核
 * 后端接口：/api/ccb-ai/review/flow/reviewSubmit
 */
export async function reviewFlowReviewSubmit(
  body: ReviewFlowReviewSubmitParam,
  options?: { [key: string]: any },
) {
  return request<{
    success?: boolean;
    message?: string;
    result?: number;
  }>('/ccb-ai/review/flow/reviewSubmit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/**
 * 获取当前流程的审核人配置（包含机构信息）
 * @param params 任务ID参数
 * @param options 请求选项
 * @returns 审核人配置响应
 */
export async function getCurrentAssignUsersWithOrg(
  params: { taskId: string | number },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseMapStringTaskAssignUserDto>('/admin/taskReportFlow/getCurrentAssignUsersWithOrg', {
    method: 'GET',
    params,
    skipErrorHandler: true,
    ...(options || {}),
  });
}

/**
 * 获取上一次保存的审核人配置（包含机构信息）
 * @param params 任务ID参数
 * @param options 请求选项
 * @returns 审核人配置响应
 */
export async function getLastAssignUsersWithOrg(
  params: { taskId: string | number },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseMapStringTaskAssignUserDto>('/admin/taskReportFlow/getLastAssignUsersWithOrg', {
    method: 'GET',
    params,
    skipErrorHandler: true,
    ...(options || {}),
  });
}

/**
 * 获取任务详情
 * @param taskId 任务ID
 * @param options 请求选项
 * @returns 任务详情响应
 */
export async function getTaskDetail(
  taskId: string | number,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseTaskRecordDetailVO>('/admin/task/detail', {
    method: 'GET',
    params: { taskId },
    ...(options || {}),
  });
}

/** 与小程序提交审核同一接口：GET /ccb-ai/task/detail */
export async function getCcbTaskDetail(
  taskId: string | number,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseTaskRecordDetailVO>('/ccb-ai/task/detail', {
    method: 'GET',
    params: { taskId },
    skipErrorHandler: true,
    ...(options || {}),
  });
}

/**
 * 查询报告列表（带流程）
 * @param params 查询参数
 * @param options 请求选项
 * @returns 报告列表响应
 */
export async function queryReportList(
  params: API.TaskReportQueryParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseStandardPageTaskReportQueryVO>('/admin/taskReportFlow/queryReportList', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 我的待处理审核数量 GET /api/admin/taskReportFlow/pending/count */
export async function getPendingReviewCount(options?: { [key: string]: any }) {
  return request<{
    httpStatusCode?: number;
    statusCode?: string | number;
    message?: string;
    result?: number | string;
    success?: boolean;
  }>('/admin/taskReportFlow/pending/count', {
    method: 'GET',
    skipErrorHandler: true,
    ...(options || {}),
  });
}

/** 我的待处理任务数量 GET /api/ccb-ai/inquiry/task/pendingCount */
export async function getPendingTaskCount(options?: { [key: string]: any }) {
  return request<{
    httpStatusCode?: number;
    statusCode?: string | number;
    message?: string;
    detailMessage?: string | null;
    result?: number | string;
    success?: boolean;
  }>('/ccb-ai/inquiry/task/pendingCount', {
    method: 'GET',
    skipErrorHandler: true,
    ...(options || {}),
  });
}

/**
 * 查询报告列表V2（带流程、已办结，排除用户关联）
 * @param params 查询参数
 * @param options 请求选项
 * @returns 报告列表响应
 */
export async function queryArchivedReportListV2(
  params: API.TaskReportQueryParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseStandardPageTaskReportQueryVO>('/admin/taskReportFlow/queryArchivedReportList/v2', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 手动刷新 Word/PDF 报告 POST /api/ccb-ai/pdfAndWord/refresh */
export async function refreshTaskReport(
  body: {
    taskId: number | string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>('/ccb-ai/pdfAndWord/refresh', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 任务报告异步刷新作业响应。 */
export interface TaskReportRefreshJobVO {
  jobId?: number | string;
  taskId?: number | string;
  status?: 'PENDING' | 'RUNNING' | 'WAITING_CONFIRMATION' | 'SUCCESS' | 'FAILED';
  wordFileId?: number | string | null;
  pdfFileId?: number | string | null;
  multiProperty?: boolean;
  generationId?: number | string;
  bodyOnlyPdfFileId?: number | string | null;
  fullPdfFileId?: number | string | null;
  files?: Array<{
    fileId?: number | string;
    variant?: 'FULL' | 'BODY_ONLY';
    fileName?: string;
    sizeBytes?: number;
    signatureStatus?: 'UNSIGNED' | 'SIGNED' | 'PREVIEW';
  }>;
  submittedTime?: number | string | null;
  startedTime?: number | string | null;
  finishedTime?: number | string | null;
  errorMessage?: string | null;
}

/** 异步刷新 Word/PDF 报告 POST /api/ccb-ai/pdfAndWord/refresh/async */
export async function refreshTaskReportAsync(
  body: {
    taskId: number | string;
  },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<TaskReportRefreshJobVO>>('/ccb-ai/pdfAndWord/refresh/async', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 查询异步刷新 Word/PDF 作业 GET /api/ccb-ai/pdfAndWord/refresh/jobs/{jobId} */
export async function getTaskReportRefreshJob(
  jobId: number | string,
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<TaskReportRefreshJobVO>>(`/ccb-ai/pdfAndWord/refresh/jobs/${jobId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/**
 * 线上取号任务上传 Word 并按模板识别回填。
 * POST /api/ccb-ai/report/onlineTakeNumber/upload
 * 同时传 fileId 与 file 时只传 fileId。
 */
export async function uploadOnlineTakeNumberReport(
  params: {
    taskId: number | string;
    fileId?: number | string;
    file?: File;
  },
  options?: { [key: string]: any },
) {
  const formData = new FormData();
  formData.append('taskId', String(params.taskId));
  if (params.fileId != null && String(params.fileId) !== '') {
    formData.append('fileId', String(params.fileId));
  } else if (params.file) {
    formData.append('file', params.file);
  }

  return request<API.UnifiedResponse<API.OnlineTakeNumberReportUploadVO>>(
    '/ccb-ai/report/onlineTakeNumber/upload',
    {
      method: 'POST',
      data: formData,
      timeout: 180 * 1000,
      ...(options || {}),
    },
  );
}

/** 上传任务报告（仅任务当前操作人）POST /api/ccb-ai/report/upload */
export async function uploadTaskReport(
  params: {
    taskId: number | string;
    file: File;
  },
  options?: { [key: string]: any },
) {
  const formData = new FormData();
  formData.append('file', params.file);

  const queryString = new URLSearchParams();
  queryString.append('taskId', String(params.taskId));

  return request<API.UnifiedResponse<number>>(`/ccb-ai/report/upload?${queryString.toString()}`, {
    method: 'POST',
    data: formData,
    ...(options || {}),
  });
}

/** 查询任务报告上传记录 GET /api/ccb-ai/report/upload/records */
export async function queryTaskReportUploadRecords(
  params: {
    taskId: number | string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseListTaskReportUploadRecordVO>('/ccb-ai/report/upload/records', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 修正报告对比 POST /api/ccb-ai/report/correction/diff */
export async function correctionReportDiff(
  params: {
    taskId: number | string;
    fileId: number | string;
  },
  options?: { [key: string]: any },
) {
  const formData = new FormData();
  formData.append('taskId', String(params.taskId));
  formData.append('fileId', String(params.fileId));

  return request<API.UnifiedResponse<API.CorrectionReportConfirmPO>>(
    '/ccb-ai/report/correction/diff',
    {
      method: 'POST',
      data: formData,
      ...(options || {}),
    },
  );
}

/** 修正报告确认 POST /api/ccb-ai/report/correction/confirm */
export async function correctionReportConfirm(
  body: API.CorrectionReportConfirmPO,
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<number>>('/ccb-ai/report/correction/confirm', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/**
 * 获取机构树形结构
 * @param params 查询参数
 * @param options 请求选项
 */
export async function getOrganizationTree(
  params?: { parentId?: number },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseListOrganizationTreeNodeVO>('/admin/organization/tree', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/**
 * 获取机构用户信息列表（包含机构信息）
 * @param params 查询参数
 * @param options 请求选项
 */
export async function getUsersByOrganizationWithOrg(
  params: {
    organizationId: number | string;
    cname?: string;
    page?: number;
    pageSize?: number;
    node?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseStandardPageOrganizationUserWithOrgVO>('/admin/taskReportFlow/getUsersByOrganizationWithOrg', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

// 保存比价资料（多套须传 propertyId；多套按集合替换语义提交五类数组）
export async function saveComparativePrices(
  params: {
    taskId: number | string;
    propertyId?: number | string;
    comparativePrices: API.CommunityTransactionInfo[];
  },
  options?: { [key: string]: any },
) {
  // 根据type字段分组：9或10为挂牌，其他为成交
  const transactionItems: API.CommunityTransactionInfo[] = [];
  const listingSameItems: API.CommunityTransactionInfo[] = [];
  const listingNearItems: API.CommunityTransactionInfo[] = [];

  params.comparativePrices.forEach(item => {
    if (item.type === 9) {
      listingSameItems.push(item);
    } else if (item.type === 10) {
      listingNearItems.push(item);
    } else {
      // 默认为成交记录（0-同小区成交，1-周边成交，2-同区域成交）
      transactionItems.push(item);
    }
  });

  const hasPropertyId = params.propertyId != null && String(params.propertyId).trim() !== '';
  const requestData: any = {
    type: 1, // 保存操作
    taskId: params.taskId,
    ...(hasPropertyId ? { propertyId: String(params.propertyId) } : {}),
  };

  if (hasPropertyId) {
    // 多套：集合替换，省略数组会清空对应分组，需显式提交五类
    requestData.sameCommunityInfo = [];
    requestData.nearCommunityInfo = [];
    requestData.sameAreaCommunityInfo = transactionItems;
    requestData.sameListingCommunityInfo = listingSameItems;
    requestData.nearListingCommunityInfo = listingNearItems;
  } else {
    // 单套：保持原行为，仅提交非空分组
    if (transactionItems.length > 0) {
      requestData.sameAreaCommunityInfo = transactionItems;
    }
    const listingItems = [...listingSameItems, ...listingNearItems];
    if (listingItems.length > 0) {
      requestData.sameListingCommunityInfo = listingItems;
    }
  }

  return request<API.WebResponseBoolean>('/admin/surroundings/del/info/update', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: requestData,
    ...(options || {}),
  });
}

/**
 * 任务关联文件打包下载
 * 成功时直接返回 ZIP 二进制流，不使用 WebResponse 包装
 */
export async function downloadTaskFilesArchive(
  taskId: number | string,
  options?: { [key: string]: any },
) {
  return request<Blob>('/ccb-ai/task/files/archive', {
    method: 'GET',
    params: { taskId },
    responseType: 'blob',
    ...(options || {}),
  });
}

/** 获取流程记录 GET /api/admin/taskReportFlow/getFlowRecords */
export async function getFlowRecords(
  params: {
    taskId: number| string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseListFlowRecordDto>(`/admin/taskReportFlow/getFlowRecords`, {
    method: 'GET',
    params: { ...params },
    ...(options || {}),
  });
}

/** 编辑机构用户 PUT /api/users/organization/{id} */
export async function updateOrganizationUser(
  params: {
    id: string | number;
    organizationUserParam: {
      cname: string;
      username: string;
      employeeNumber?: string;
      nickname?: string;
      gender?: number;
      description?: string;
      avatar?: string;
      enabled?: boolean;
      phone?: string;
      email?: string;
      password?: string;
      birthDate?: string;
      roleCodes: string[];
      organizationIds: (string | number)[];
      deptIds?: (string | number)[];
      practisingCertificateFileIds: (string | number)[];
      licenseNumber?: string;
      licenseValidTo?: number;
      electronicSignatureFileId?: number | string;
      electronicSealFileId?: number | string;
    };
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>(`/users/organization/${params.id}`, {
    method: 'PUT',
    data: params.organizationUserParam,
    ...(options || {}),
  });
}

/** 获取流程详情 GET /api/admin/taskReportFlow/getFlowDetail */
export async function getFlowDetail(
  params: {
    taskId: number | string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseFlowDetailDto>(`/admin/taskReportFlow/getFlowDetail`, {
    method: 'GET',
    params: { ...params },
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** 设置后续节点审核人（包含机构信息） POST /api/admin/taskReportFlow/setNextTaskAssignUsersWithOrg */
export async function setNextTaskAssignUsersWithOrg(
  params: API.CreateFlowWithAssignUsersWithOrgParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseString>(`/admin/taskReportFlow/setNextTaskAssignUsersWithOrg`, {
    method: 'POST',
    data: {
      ...params
    },
    ...(options || {}),
  });
}

/** 申请重做 POST /api/admin/taskReportFlow/requestRedo */
export async function requestRedo(
  body: {
    taskId: number | string;
    redoApproverUserId: string;
    comment?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseFlowDto>('/admin/taskReportFlow/requestRedo', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/** 办理流程（带审批意见） POST /api/admin/taskReportFlow/processFlowWithComment */
export async function processFlowWithComment(
  params: {
    taskId: number | string;
    action: string;
    comment: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseFlowDto>(`/admin/taskReportFlow/processFlowWithComment`, {
    method: 'POST',
    params: { ...params },
    ...(options || {}),
  });
}

/**
 * 根据角色获取机构用户列表
 * @param params 查询参数
 * @param options 请求选项
 * @returns 用户列表响应
 */
export async function getUsersByRole(
  params: {
    roleCode?: string;
    roleCodes?: string[];
    cname?: string;
    page?: number;
    pageSize?: number;
  },
  options?: { [key: string]: any },
) {
  const { roleCode, roleCodes, ...rest } = params;
  const queryParams: Record<string, any> = { ...rest };
  if (roleCodes?.length) {
    queryParams.roleCodes = roleCodes.filter((code) => code).join(',');
  } else if (roleCode) {
    queryParams.roleCode = roleCode;
  }
  return request<any>('/users/organization/byRole', {
    method: 'GET',
    params: queryParams,
    ...(options || {}),
  });
}

/**
 * 按角色搜索客户用户。
 * GET /api/ccb-ai/user/getCustomerUserByRole
 * 与小程序发起委托的客户经理搜索一致，cname 为姓名模糊条件。
 */
export async function getCustomerUserByRole(
  params: {
    roleCode: string;
    page?: number;
    pageSize?: number;
    cname?: string;
  },
  options?: { [key: string]: any },
) {
  const query: Record<string, string | number> = {
    roleCode: params.roleCode,
  };
  if (params.page !== undefined) query.page = params.page;
  if (params.pageSize !== undefined) query.pageSize = params.pageSize;
  const cname = params.cname?.trim();
  if (cname) query.cname = cname;
  return request<any>('/ccb-ai/user/getCustomerUserByRole', {
    method: 'GET',
    params: query,
    ...(options || {}),
  });
}

/** 添加客户用户 POST /api/users/customer */
export async function createCustomerUser(
  params: {
    cname: string;
    username: string;
    nickname?: string;
    gender?: number;
    description?: string;
    avatar?: string;
    enabled?: boolean;
    phone?: string;
    email?: string;
    password?: string;
    birthDate?: string;
    roleCodes: string[];
    organizationIds?: string | number[];
    practisingCertificateFileIds?: number[];
    customerIds: string[];
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>('/users/customer', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 编辑客户用户 PUT /api/users/customer/{id} */
export async function updateCustomerUser(
  params: {
    id: string | number;
    customerUserParam: {
      cname: string;
      username: string;
      nickname?: string;
      gender?: number;
      description?: string;
      avatar?: string;
      enabled?: boolean;
      phone?: string;
      email?: string;
      password?: string;
      birthDate?: string;
      roleCodes: string[];
      organizationIds?: (string | number)[];
      practisingCertificateFileIds?: number[];
      customerIds: (string | number)[];
    };
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>(`/users/customer/${params.id}`, {
    method: 'PUT',
    data: params.customerUserParam,
    ...(options || {}),
  });
}

/** 更换客户用户所属客户 PUT /api/users/customer/{id} */
export async function changeCustomerUserCustomer(
  id: string | number,
  customerId: string,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>(`/users/customer/${id}`, {
    method: 'PUT',
    data: { customerId },
    ...(options || {}),
  });
}

/** 获取客户用户详情 GET /api/users/customer/{id} */
export async function getCustomerUserDetail(
  id: string | number,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseCustomerUserVO>(`/users/customer/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 启用客户用户 PUT /api/users/customer/{id}/enable */
export async function enableCustomerUser(
  id: string | number,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>(`/users/customer/${id}/enable`, {
    method: 'PUT',
    ...(options || {}),
  });
}

/** 禁用客户用户 PUT /api/users/customer/{id}/disable */
export async function disableCustomerUser(
  id: string | number,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>(`/users/customer/${id}/disable`, {
    method: 'PUT',
    ...(options || {}),
  });
}

/** 删除客户用户 DELETE /api/users/customer/{id} */
export async function deleteCustomerUser(
  id: string | number,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseObject>(`/users/customer/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 判断客户用户名称是否存在 GET /api/users/customer/one/by/username */
export async function checkCustomerUsernameExists(
  username: string,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseBoolean>('/users/customer/one/by/username', {
    method: 'GET',
    params: { username },
    ...(options || {}),
  });
}

// ==================== 净值方案相关 ====================

/** 分页查询净值方案列表 GET /api/admin/netWorthPlan/page */
export async function getNetWorthPlanPage(
  params: {
    page?: number;
    pageSize?: number;
    provinceCode?: string;
    cityCode?: string;
    districtCode?: string;
    customerId?: number;
    customerName?: string;
    organizationId?: number;
    organizationName?: string;
    businessLines?: number;
    businessType?: number;
    netWorthName?: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/admin/netWorthPlan/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.NetWorthPlanPageResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 查询扣减项模板列表 GET /api/admin/netWorthPlan/deductItemTemplate（当前业务侧未调用，见 netValueSolution/add loadDeductItemTemplate） */
export async function getDeductItemTemplate(
  options?: { [key: string]: any },
) {
  return request<API.NetWorthPlanInfoResponse>('/admin/netWorthPlan/deductItemTemplate', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 新增-编辑净值方案 POST /api/admin/netWorthPlan/upsert */
export async function upsertNetWorthPlan(
  params: API.NetWorthPlanUpsertParam,
  options?: { [key: string]: any },
) {
  return request<API.NetWorthPlanInfoResponse>('/admin/netWorthPlan/upsert', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 复制为全新净值方案 POST /api/admin/netWorthPlan/createAsCopy（入参与 upsert 一致，服务端忽略各类 id） */
export async function createNetWorthPlanAsCopy(
  params: API.NetWorthPlanUpsertParam,
  options?: { [key: string]: any },
) {
  return request<API.NetWorthPlanInfoResponse>('/admin/netWorthPlan/createAsCopy', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 获取净值方案详情 GET /api/admin/netWorthPlan/detail */
export async function getNetWorthPlanInfo(
  params: {
    id: string | number;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/admin/netWorthPlan/detail${queryString.toString() ? `?${queryString.toString()}` : ''}`;
  return request<API.NetWorthPlanInfoResponse>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 计算公式参数列表 GET /api/admin/netWorthPlan/formulaParameterList?planId=（planId 可选） */
export async function getFormulaParameterList(
  params?: { planId?: string | number | null },
  options?: { [key: string]: any },
) {
  const qs = new URLSearchParams();
  if (params?.planId != null && String(params.planId).trim() !== '') {
    qs.append('planId', String(params.planId));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<API.WebResponseListFormulaParameterVO>(`/admin/netWorthPlan/formulaParameterList${suffix}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 查询自定义参数列表（按方案） GET /api/admin/netWorthPlan/customParameters?planId= */
export async function getPlanCustomParameters(
  params: { planId: string | number },
  options?: { [key: string]: any },
) {
  const qs = new URLSearchParams({ planId: String(params.planId) });
  return request<API.WebResponseListCustomParameterVO>(`/admin/netWorthPlan/customParameters?${qs.toString()}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 自定义参数详情 GET /api/admin/netWorthPlan/customParameter/detail?id= */
export async function getPlanCustomParameterDetail(
  params: { id: string | number },
  options?: { [key: string]: any },
) {
  const qs = new URLSearchParams({ id: String(params.id) });
  return request<API.WebResponseCustomParameterVO>(`/admin/netWorthPlan/customParameter/detail?${qs.toString()}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 新增-编辑方案自定义参数 POST /api/admin/netWorthPlan/customParameter/upsert */
export async function upsertPlanCustomParameter(
  body: API.PlanCustomParameterParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseCustomParameterVO>('/admin/netWorthPlan/customParameter/upsert', {
    method: 'POST',
    data: {
      ...body,
      enabled: body.enabled ?? 0,
    },
    ...(options || {}),
  });
}

/** 删除方案自定义参数 DELETE /api/admin/netWorthPlan/customParameter/{id} */
export async function deletePlanCustomParameter(
  id: string | number,
  options?: { [key: string]: any },
) {
  const idSeg = encodeURIComponent(String(id));
  return request<API.WebResponseBoolean>(`/admin/netWorthPlan/customParameter/${idSeg}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 获取所有银行列表 GET /api/admin/bank/all */
export async function getAllBanks(
  options?: { [key: string]: any },
) {
  return request<API.WebResponseListBankVO>('/admin/bank/all', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 删除净值方案 DELETE /api/admin/netWorthPlan/delete/{id} */
export async function deleteNetWorthPlan(
  id: string | number,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseBoolean>(`/admin/netWorthPlan/delete/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 通用参数列表 GET /api/admin/universalParameter/list */
export async function listUniversalParameters(options?: { [key: string]: any }) {
  return request<API.WebResponseListUniversalParameterEntity>('/admin/universalParameter/list', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 新增-编辑通用参数 POST /api/admin/universalParameter/upsert */
export async function upsertUniversalParameter(
  body: API.UniversalParameterEntity,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseBoolean>('/admin/universalParameter/upsert', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/** 删除通用参数 DELETE /api/admin/universalParameter/delete?id= */
export async function deleteUniversalParameter(
  id: number | string,
  options?: { [key: string]: any },
) {
  const qs = new URLSearchParams({ id: String(id) });
  return request<API.WebResponseBoolean>(`/admin/universalParameter/delete?${qs.toString()}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 通用参数详情 GET /api/admin/universalParameter/detail?id= */
export async function getUniversalParameterDetail(
  id: number | string,
  options?: { [key: string]: any },
) {
  const qs = new URLSearchParams({ id: String(id) });
  return request<API.WebResponseUniversalParameterEntity>(
    `/admin/universalParameter/detail?${qs.toString()}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

// ==================== 台账管理相关 ====================

/**
 * 分页查询台账记录 POST /api/admin/ledger-record/page
 *
 * 高级查询：请求体可包含 `ledgerAdvancedSearch`，与上方筛选条件一并提交（建议后端按「普通条件 AND 高级表达式」解析）。
 * 结构见前端 `LedgerAdvancedSearchPayload`（`src/pages/ledger/list/advancedSearchUtils.ts`）。
 */
export async function getLedgerList(
  params: {
    page?: number;
    pageSize?: number;
    /** 高级查询条件（可选），与台账列表页「高级查询」弹窗一致 */
    ledgerAdvancedSearch?: {
      criteria: Array<{
        field: string;
        operator: string;
        value?: unknown;
        valueEnd?: unknown;
      }>;
      joins: string[];
    };
    [key: string]: any;
  },
  options?: { [key: string]: any },
) {
  // 构建请求体，包含查询条件和分页参数
  const requestBody: any = {};

  // 提取分页参数
  const page = params.page;
  const pageSize = params.pageSize;
  delete params.page;
  delete params.pageSize;

  // 将其他参数放入请求体
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      requestBody[key] = value;
    }
  });

  // 构建查询字符串（分页参数）
  const queryString = new URLSearchParams();
  if (page !== undefined) {
    queryString.append('page', String(page));
  }
  if (pageSize !== undefined) {
    queryString.append('pageSize', String(pageSize));
  }

  const url = `/admin/ledger-record/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.WebResponseStandardPageLedgerRecordEntity>(url, {
    method: 'POST',
    data: requestBody,
    headers: {
      'Content-Type': 'application/json',
    },
    ...(options || {}),
  });
}

/** 询价台账分页 POST /admin/inquiry-ledger/page?page=&pageSize= */
export async function getInquiryLedgerPage(
  params: {
    page?: number;
    pageSize?: number;
    [key: string]: any;
  },
  options?: { [key: string]: any },
) {
  const requestBody: Record<string, any> = {};
  const page = params.page;
  const pageSize = params.pageSize;
  const { page: _page, pageSize: _pageSize, ...rest } = params;

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      requestBody[key] = value;
    }
  });

  const queryString = new URLSearchParams();
  if (page !== undefined) {
    queryString.append('page', String(page));
  }
  if (pageSize !== undefined) {
    queryString.append('pageSize', String(pageSize));
  }

  const url = `/admin/inquiry-ledger/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.WebResponseStandardPageInquiryLedgerRecordVO>(url, {
    method: 'POST',
    data: requestBody,
    headers: {
      'Content-Type': 'application/json',
    },
    ...(options || {}),
  });
}

/** 询价台账详情 GET /admin/inquiry-ledger/detail?id= */
export async function getInquiryLedgerDetail(
  params: { id: string | number },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseInquiryLedgerInfoVO>('/admin/inquiry-ledger/detail', {
    method: 'GET',
    params: { id: String(params.id) },
    ...(options || {}),
  });
}

/** 人工修订询价台账 POST /admin/inquiry-ledger/edit */
export async function editInquiryLedger(
  body: {
    id: string | number;
    version: string | number;
    reason: string;
    changes: Record<string, any>;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseInquiryLedgerInfoVO>('/admin/inquiry-ledger/edit', {
    method: 'POST',
    data: {
      id: String(body.id),
      version: String(body.version),
      reason: body.reason,
      changes: body.changes,
    },
    headers: {
      'Content-Type': 'application/json',
    },
    ...(options || {}),
  });
}

/** 恢复询价台账业务值 POST /admin/inquiry-ledger/reset-fields */
export async function resetInquiryLedgerFields(
  body: {
    id: string | number;
    version: string | number;
    reason: string;
    fields: string[];
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseInquiryLedgerInfoVO>('/admin/inquiry-ledger/reset-fields', {
    method: 'POST',
    data: {
      id: String(body.id),
      version: String(body.version),
      reason: body.reason,
      fields: body.fields,
    },
    headers: {
      'Content-Type': 'application/json',
    },
    ...(options || {}),
  });
}

/** 作废询价台账 POST /admin/inquiry-ledger/void */
export async function voidInquiryLedger(
  body: {
    id: string | number;
    version: string | number;
    reason: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseInquiryLedgerInfoVO>('/admin/inquiry-ledger/void', {
    method: 'POST',
    data: {
      id: String(body.id),
      version: String(body.version),
      reason: body.reason,
    },
    headers: {
      'Content-Type': 'application/json',
    },
    ...(options || {}),
  });
}

/** 恢复已作废询价台账 POST /admin/inquiry-ledger/restore */
export async function restoreInquiryLedger(
  body: {
    id: string | number;
    version: string | number;
    reason: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseInquiryLedgerInfoVO>('/admin/inquiry-ledger/restore', {
    method: 'POST',
    data: {
      id: String(body.id),
      version: String(body.version),
      reason: body.reason,
    },
    headers: {
      'Content-Type': 'application/json',
    },
    ...(options || {}),
  });
}

/** 询价台账历次复核 GET /admin/inquiry-ledger/reviews */
export async function getInquiryLedgerReviews(
  params: {
    id: string | number;
    page?: number;
    pageSize?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseStandardPageInquiryLedgerReviewVO>('/admin/inquiry-ledger/reviews', {
    method: 'GET',
    params: {
      id: String(params.id),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 10,
    },
    ...(options || {}),
  });
}

/** 询价台账关联附件 GET /admin/inquiry-ledger/files */
export async function getInquiryLedgerFiles(
  params: {
    id: string | number;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseInquiryLedgerReviewFilesVO>('/admin/inquiry-ledger/files', {
    method: 'GET',
    params: {
      id: String(params.id),
    },
    ...(options || {}),
  });
}

/** 询价台账操作记录 GET /admin/inquiry-ledger/operations */
export async function getInquiryLedgerOperations(
  params: {
    id: string | number;
    page?: number;
    pageSize?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseStandardPageInquiryLedgerOperationVO>(
    '/admin/inquiry-ledger/operations',
    {
      method: 'GET',
      params: {
        id: String(params.id),
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 10,
      },
      ...(options || {}),
    },
  );
}

/** 获取台账详情 GET /api/admin/ledger-record/detail */
export async function getLedgerDetail(
  params: {
    id: string | number;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryString.append(key, String(value));
    }
  });

  const url = `/admin/ledger-record/detail${queryString.toString() ? `?${queryString.toString()}` : ''}`;

  return request<API.UnifiedResponse<any>>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 新增/编辑台账记录 POST /api/admin/ledger-record/upsert */
export async function upsertLedgerRecord(
  body: {
    id?: number;
    [key: string]: any;
  },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<any>>('/admin/ledger-record/upsert', {
    method: 'POST',
    data: body,
    headers: {
      'Content-Type': 'application/json',
    },
    ...(options || {}),
  });
}

/** 导入台账记录 POST /api/admin/ledger-record/import */
export async function importLedgerRecord(
  file: File,
  options?: { [key: string]: any },
) {
  const formData = new FormData();
  formData.append('file', file);

  return request<API.UnifiedResponse>('/admin/ledger-record/import', {
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...(options || {}),
  });
}

/**
 * 导出台账记录到Excel POST /api/admin/ledger-record/excel
 * 请求体字段与分页查询一致时可包含 `ledgerAdvancedSearch`。
 */
export async function exportLedgerRecord(
  params: {
    /** 按顺序指定导出的台账表头字段。 */
    fieldKeys: string[];
    ledgerAdvancedSearch?: {
      criteria: Array<{ field: string; operator: string; value?: unknown; valueEnd?: unknown }>;
      joins: string[];
    };
    [key: string]: any;
  },
  options?: { [key: string]: any },
) {
  return request<Blob>('/admin/ledger-record/excel', {
    method: 'POST',
    data: params,
    headers: {
      'Content-Type': 'application/json',
    },
    responseType: 'blob',
    ...(options || {}),
  });
}

/** 同步台账到咕咕-初始化 POST /api/admin/ledger-record/sync-to-gugu/init */
export async function syncLedgerRecordToGuguInit(
  params: {
    /** 台账记录ID */
    id: number;
    /** 用户token（用于第三方服务认证） */
    token: string;
    /** 文件（可选，如果传文件则优先使用上传的文件） */
    file?: File;
  },
  options?: { [key: string]: any },
) {
  const formData = new FormData();
  formData.append('id', String(params.id));
  formData.append('token', params.token);
  if (params.file) formData.append('file', params.file);
  return request<API.WebResponseSyncToGuGuProgressVO>('/admin/ledger-record/sync-to-gugu/init', {
    method: 'POST',
    data: formData,
    ...(options || {}),
  });
}

/** 同步台账到咕咕-保存与绑定 POST /api/admin/ledger-record/sync-to-gugu/save-and-bind */
export async function syncLedgerRecordToGuguSaveAndBind(
  params: {
    /** 台账记录ID */
    id: number;
    /** 文件ID（来自 init 返回） */
    fileId: string;
    /** 是否绑定文件 */
    bandFile: boolean;
    /** 用户token（用于第三方服务认证） */
    token: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  queryString.append('id', String(params.id));
  queryString.append('fileId', params.fileId);
  queryString.append('bandFile', String(params.bandFile));
  queryString.append('token', params.token);
  const url = `/admin/ledger-record/sync-to-gugu/save-and-bind?${queryString.toString()}`;
  return request<API.WebResponseSyncToGuGuProgressVO>(url, {
    method: 'POST',
    ...(options || {}),
  });
}

/** 同步台账到咕咕-转交审核单次 POST /api/admin/ledger-record/sync-to-gugu/flow-once */
export async function syncLedgerRecordToGuguFlowOnce(
  params: {
    /** 台账记录ID */
    id: number;
    /** 用户token（用于第三方服务认证） */
    token: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  queryString.append('id', String(params.id));
  queryString.append('token', params.token);
  const url = `/admin/ledger-record/sync-to-gugu/flow-once?${queryString.toString()}`;
  return request<API.WebResponseSyncToGuGuProgressVO>(url, {
    method: 'POST',
    ...(options || {}),
  });
}

/** 同步台账到咕咕-编辑并直连 POST /api/admin/ledger-record/sync-to-gugu/submit */
export async function syncLedgerRecordToGuguSubmit(
  params: {
    /** 台账记录ID */
    id: number;
    /** 用户token（用于第三方服务认证） */
    token: string;
  },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  queryString.append('id', String(params.id));
  queryString.append('token', params.token);
  const url = `/admin/ledger-record/sync-to-gugu/submit?${queryString.toString()}`;
  return request<API.WebResponseLedgerRecordInfoVO>(url, {
    method: 'POST',
    ...(options || {}),
  });
}

/**
 * 创建批量直连任务 POST /api/admin/ledger-record/direct-connect-tasks/batch
 * 单条直连传 ledgerRecordIds: [id] 即可
 */
export async function createBatchDirectConnectTask(
  params: API.GuguDirectConnectBatchCreateParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseGuguDirectConnectTaskVO>(
    '/admin/ledger-record/direct-connect-tasks/batch',
    {
      method: 'POST',
      data: params,
      headers: { 'Content-Type': 'application/json' },
      ...(options || {}),
    },
  );
}

/** 创建自动直连任务 POST /api/admin/ledger-record/direct-connect-tasks/auto */
export async function createAutoDirectConnectTask(
  params: API.GuguDirectConnectAutoCreateParam,
  options?: { [key: string]: any },
) {
  return request<API.WebResponseGuguDirectConnectTaskVO>(
    '/admin/ledger-record/direct-connect-tasks/auto',
    {
      method: 'POST',
      data: params,
      headers: { 'Content-Type': 'application/json' },
      ...(options || {}),
    },
  );
}

/** 分页查询直连任务 GET /api/admin/ledger-record/direct-connect-tasks/page */
export async function getDirectConnectTaskPage(
  params: { page?: number; pageSize?: number } = {},
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  if (params.page != null) queryString.append('page', String(params.page));
  if (params.pageSize != null) queryString.append('pageSize', String(params.pageSize));
  const url = `/admin/ledger-record/direct-connect-tasks/page${queryString.toString() ? `?${queryString.toString()}` : ''}`;
  return request<API.WebResponseStandardPageGuguDirectConnectTaskVO>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 查询直连任务详情 GET /api/admin/ledger-record/direct-connect-tasks/{id} */
export async function getDirectConnectTaskDetail(
  id: string | number,
  options?: { [key: string]: any },
) {
  const idSeg = encodeURIComponent(String(id));
  return request<API.WebResponseGuguDirectConnectTaskDetailVO>(
    `/admin/ledger-record/direct-connect-tasks/${idSeg}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/** 终止排队中的直连任务 POST /api/admin/ledger-record/direct-connect-tasks/{id}/cancel */
export async function cancelDirectConnectTask(
  id: string | number,
  params?: { reason?: string },
  options?: { [key: string]: any },
) {
  const queryString = new URLSearchParams();
  if (params?.reason) queryString.append('reason', params.reason);
  const idSeg = encodeURIComponent(String(id));
  const url = `/admin/ledger-record/direct-connect-tasks/${idSeg}/cancel${queryString.toString() ? `?${queryString.toString()}` : ''}`;
  return request<API.WebResponseBoolean>(url, {
    method: 'POST',
    ...(options || {}),
  });
}

/** 流式智能体识别接口 POST /api/admin/cert/cert_recognize */
export async function streamCertRecognize(
  data: {
    images: string[];
    stream: boolean;
    rec_mode: 'normal' | 'fast_info';
  },
  callbacks: {
    onChunk: (content: string) => void;
    onError?: (error: Error) => void;
    onComplete?: () => void;
    onResultData?: (resultData: any) => void;
  },
  abortSignal?: AbortSignal,
  recognizePath = '/admin/cert/cert_recognize',
): Promise<void> {


  const url = buildApiPath(recognizePath);

  try {
    await fetchEventSource(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      signal: abortSignal,
      async onopen(response) {
        // 检查响应状态和Content-Type
        if (!response.ok) {
          const statusText = response.statusText || `HTTP ${response.status}`;
          const errorMsg = `接口请求失败: ${statusText} (${response.status})`;
          if (callbacks.onError) {
            callbacks.onError(new Error(errorMsg));
          }
          throw new Error(errorMsg);
        }
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('text/event-stream') && !contentType.includes('text/plain')) {
          // 如果不是SSE流，可能是404或其他错误
          const errorMsg = `接口返回格式错误: 期望 text/event-stream，实际 ${contentType}`;
          if (callbacks.onError) {
            callbacks.onError(new Error(errorMsg));
          }
          throw new Error(errorMsg);
        }
      },
      onmessage(event) {
        try {
          // 解析SSE格式的数据
          const jsonData = JSON.parse(event.data);
          const eventType = jsonData.event || '';
          const eventData = jsonData.data;

          if (eventType === 'done') {
            const resultType = eventData?.type || '';
            if (resultType === 'done') {
              // 最终结果
              if (callbacks.onResultData && eventData) {
                callbacks.onResultData(eventData);
              }
              // 如果有results数据，格式化后显示出来
              if (eventData?.results) {
                const resultText = '\n\n久等了，处理已完成，请点击"确认继续"～';
                if (resultText.trim()) {
                  callbacks.onChunk(resultText);
                }
              }
              if (callbacks.onComplete) {
                callbacks.onComplete();
              }
            } else if (resultType === 'error') {
              const errorMsg = eventData?.error || eventData?.message || '未知错误';
              if (callbacks.onError) {
                callbacks.onError(new Error(errorMsg));
              }
            }
          } else if (eventType === 'error') {
            // 直接处理 error 事件
            const errorMsg = typeof eventData === 'string'
              ? eventData
              : eventData?.error || eventData?.message || '未知错误';
            if (callbacks.onError) {
              callbacks.onError(new Error(errorMsg));
            }
          } else if (eventType === 'chunk') {
            // chunk 事件，立即实时输出
            const chunkText = typeof eventData === 'string' ? eventData : String(eventData);
            callbacks.onChunk(chunkText);
          } else if (eventType === 'message') {
            // message 事件的 data 是字符串
            const messageText = typeof eventData === 'string' ? eventData : String(eventData);
            callbacks.onChunk(`${messageText}\n`);
          }
        } catch (e) {
          // JSON解析失败，静默处理
          console.warn('解析 SSE 数据失败:', e, event.data);
        }
      },
      onerror(err) {
        // 处理流式读取过程中的错误
        const errorMsg = err instanceof Error ? err.message : '流式响应读取失败';
        if (callbacks.onError) {
          callbacks.onError(new Error(errorMsg));
        }
        throw err;
      },
    });
  } catch (error: unknown) {
    if (callbacks.onError) {
      callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    } else {
      throw error;
    }
  }
}

/** 保存证件识别结果 POST /api/admin/customerTask/ocr/save */
export async function saveCertRecognizeResult(
  body: {
    results: any;
    taskId?: string;
    sourceType?: 'CUSTOMER_TASK';
    sourceId?: string;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success?: boolean;
    message?: string;
    result?: any;
    [key: string]: any;
  }>('/admin/customerTask/ocr/save', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 批量获取文件shareUri POST /api/admin/files/share/files/share/batch */
export async function getFileShareUrisBatch(
  body: Array<{
    id: string | number;
    effectiveTime: number;
    expirationTime: number;
    clientId: string;
  }>,
  options?: { [key: string]: any },
) {
  return request<{
    success?: boolean;
    message?: string;
    result?: Array<{
      id: number;
      effectiveTime: number;
      expirationTime: number;
      uri: string;
      token: string;
      clientId: string;
      storage?: {
        shareUri?: string;
        [key: string]: any;
      };
      [key: string]: any;
    }>;
    [key: string]: any;
  }>('/files/share/files/share/batch', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 解析OCR识别的结果，返回解析后的权属信息 POST /api/admin/customerTask/ocr/structuring */
// export async function ocrStructuring(
//   body: {
//     results: {
//       location?: any;
//       warnings?: any[];
//       certs?: any[];
//     };
//   },
//   options?: { [key: string]: any },
// ) {
//   return request<API.WebResponseOwnershipOcrInfoVO>('/admin/customerTask/ocr/structuring', {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//     data: body,
//     ...(options || {}),
//   });
// }
export async function ocrStructuring(
  body: {
    results: {
      location?: any;
      warnings?: any[];
      certs?: any[];
    };
    taskId?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.WebResponseOwnershipOcrInfoVO>('admin/task/ocr/saveToTask', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}


// ==================== 复核流程相关 ====================

/** 获取复核流程记录 GET /api/admin/review/getFlowRecords */
export async function getReviewFlowRecords(
  params: {
    reviewId: number | string;
  },
  options?: { [key: string]: any },
) {
  return request<WebResponseListReviewFlowRecordExtDto>('/admin/review/getFlowRecords', {
    method: 'GET',
    params: { ...params },
    ...(options || {}),
  });
}

// ==================== 批量询价 ====================

function normalizeInquiryBatchTaskItem(
  item: InquiryBatchTaskItemVO & { inquiryId?: unknown },
): InquiryBatchTaskItemVO {
  return {
    ...item,
    inquiryId: item.inquiryId != null ? String(item.inquiryId) : null,
  };
}

function normalizeInquiryBatchTask(
  task: InquiryBatchTaskVO & {
    inquiryBatchId?: unknown;
    sourceFileId?: unknown;
    resultFileId?: unknown;
    items?: Array<InquiryBatchTaskItemVO & { inquiryId?: unknown }>;
  },
): InquiryBatchTaskVO {
  return {
    ...task,
    inquiryBatchId: String(task.inquiryBatchId),
    sourceFileId: task.sourceFileId != null ? String(task.sourceFileId) : null,
    resultFileId: task.resultFileId != null ? String(task.resultFileId) : null,
    items: task.items?.map(normalizeInquiryBatchTaskItem),
  };
}

function normalizeInquiryBatchImport(
  result: InquiryBatchImportVO & {
    inquiryBatchId?: unknown;
    existingInquiryBatchId?: unknown;
  },
): InquiryBatchImportVO {
  return {
    ...result,
    inquiryBatchId: String(result.inquiryBatchId),
    existingInquiryBatchId:
      result.existingInquiryBatchId != null ? String(result.existingInquiryBatchId) : null,
  };
}

/** 下载批量询价导入模板 GET /api/admin/inquiry-batch/template */
export async function downloadInquiryBatchTemplate(options?: { [key: string]: any }) {
  return request<Blob>('/admin/inquiry-batch/template', {
    method: 'GET',
    responseType: 'blob',
    ...(options || {}),
  });
}

/** 上传 Excel 创建批量询价任务 POST /api/admin/inquiry-batch/import */
export async function importInquiryBatch(file: File, options?: { [key: string]: any }) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await request<WebResponseInquiryBatchImportVO>('/admin/inquiry-batch/import', {
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    skipErrorHandler: true,
    ...(options || {}),
  });

  if (response?.result) {
    return {
      ...response,
      result: normalizeInquiryBatchImport(response.result),
    };
  }

  return response;
}

/** 查询批量询价任务进度 GET /api/admin/inquiry-batch/batch/{inquiryBatchId} */
export async function getInquiryBatchDetail(
  inquiryBatchId: string,
  options?: { [key: string]: any },
) {
  const response = await request<WebResponseInquiryBatchTaskVO>(
    `/admin/inquiry-batch/batch/${inquiryBatchId}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );

  if (response?.result) {
    return {
      ...response,
      result: normalizeInquiryBatchTask(response.result),
    };
  }

  return response;
}

/** 下载批量询价结果 Excel GET /api/admin/inquiry-batch/batch/{inquiryBatchId}/download */
export async function downloadInquiryBatchResult(
  inquiryBatchId: string,
  options?: { [key: string]: any },
) {
  return request<Blob>(`/admin/inquiry-batch/batch/${inquiryBatchId}/download`, {
    method: 'GET',
    responseType: 'blob',
    ...(options || {}),
  });
}

/** 当前用户批量询价任务分页 POST /api/admin/inquiry-batch/batch/list */
export async function getInquiryBatchList(
  params: import('@/types/api').InquiryBatchListQueryParams = {},
  options?: { [key: string]: any },
) {
  const response = await request<WebResponseStandardPageInquiryBatchTaskVO>(
    '/admin/inquiry-batch/batch/list',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        page: params.page,
        pageSize: params.pageSize,
        status: params.status,
        createTimeStart: params.createTimeStart,
        createTimeEnd: params.createTimeEnd,
        updateTimeStart: params.updateTimeStart,
        updateTimeEnd: params.updateTimeEnd,
        // ID 保持字符串，避免 long 精度丢失
        userId: params.userId != null ? String(params.userId) : undefined,
        creator: params.creator != null ? String(params.creator) : undefined,
      },
      ...(options || {}),
    },
  );

  if (response?.result?.list) {
    return {
      ...response,
      result: {
        ...response.result,
        list: response.result.list.map(normalizeInquiryBatchTask),
      },
    };
  }

  return response;
}

// ==================== 业务子类型 ====================

/** 三级联动选择接口 GET /ccb-ai/business-sub-type/options */
export async function getBusinessSubTypeOptions(
  params: {
    businessLines: number;
    businessType: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<BusinessSubTypeOptionVO[]>>(
    '/ccb-ai/business-sub-type/options',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

/** 分页查询业务子类型配置 GET /admin/business-sub-type/page */
export async function getBusinessSubTypePage(
  params?: {
    page?: number;
    pageSize?: number;
    name?: string;
    businessLines?: number;
    businessType?: number;
    status?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<StandardPageBusinessSubTypeEntity>>(
    '/admin/business-sub-type/page',
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

/** 查询业务子类型详情 GET /admin/business-sub-type/detail */
export async function getBusinessSubTypeDetail(
  id: number | string,
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<BusinessSubTypeEntity>>(
    '/admin/business-sub-type/detail',
    {
      method: 'GET',
      params: { id },
      ...(options || {}),
    },
  );
}

/** 新增或编辑业务子类型 POST /admin/business-sub-type/upsert */
export async function upsertBusinessSubType(
  body: BusinessSubTypeUpsertParam,
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<BusinessSubTypeEntity>>(
    '/admin/business-sub-type/upsert',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
}

/** 启用或停用业务子类型 PUT /admin/business-sub-type/status/{id} */
export async function updateBusinessSubTypeStatus(
  id: number | string,
  body: { status: 0 | 1 },
  options?: { [key: string]: any },
) {
  return request<API.UnifiedResponse<boolean>>(`/admin/business-sub-type/status/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

// ==================== 权属识别评测 ====================

export async function certEvalExtractSamples(body: { taskIds?: Array<number | string>; limit?: number }) {
  return request<API.UnifiedResponse<number>>('/admin/cert-eval/samples/extract', {
    method: 'POST',
    data: body,
  });
}

export async function certEvalPageCandidates(params?: {
  page?: number;
  pageSize?: number;
  taskId?: number | string;
  electronicReportCode?: string;
}) {
  return request<API.UnifiedResponse<{ list?: any[]; total?: number }>>(
    '/admin/cert-eval/samples/candidates',
    {
      method: 'GET',
      params,
    },
  );
}

export async function certEvalAddSamples(body: { taskIds: Array<number | string> }) {
  return request<API.UnifiedResponse<number>>('/admin/cert-eval/samples/add', {
    method: 'POST',
    data: body,
  });
}

export async function certEvalReactivateSample(id: number | string) {
  return request<API.UnifiedResponse<unknown>>(`/admin/cert-eval/samples/${id}/reactivate`, {
    method: 'POST',
  });
}

export async function certEvalPageSamples(params?: {
  page?: number;
  pageSize?: number;
  status?: number;
  taskId?: number | string;
}) {
  return request<API.UnifiedResponse<{ list?: any[]; total?: number }>>('/admin/cert-eval/samples/page', {
    method: 'GET',
    params,
  });
}

export async function certEvalInvalidateSample(id: number | string, body: { invalidReason: string }) {
  return request<API.UnifiedResponse<unknown>>(`/admin/cert-eval/samples/${id}/invalidate`, {
    method: 'POST',
    data: body,
  });
}

export async function certEvalRemoveSample(id: number | string) {
  return request<API.UnifiedResponse<unknown>>(`/admin/cert-eval/samples/${id}`, {
    method: 'DELETE',
  });
}

export async function certEvalBatchRemoveSamples(body: { ids: Array<number | string> }) {
  return request<API.UnifiedResponse<number>>('/admin/cert-eval/samples/batch-remove', {
    method: 'POST',
    data: body,
  });
}

export async function certEvalRefreshGold(id: number | string) {
  return request<API.UnifiedResponse<unknown>>(`/admin/cert-eval/samples/${id}/refresh-gold`, {
    method: 'POST',
  });
}

export async function certEvalCreateSet(body: {
  name: string;
  defaultRecMode?: string;
  remark?: string;
  sampleIds: Array<number | string>;
}) {
  return request<API.UnifiedResponse<any>>('/admin/cert-eval/sets', {
    method: 'POST',
    data: body,
  });
}

export async function certEvalPageSets(params?: { page?: number; pageSize?: number }) {
  return request<API.UnifiedResponse<{ list?: any[]; total?: number }>>('/admin/cert-eval/sets/page', {
    method: 'GET',
    params,
  });
}

export async function certEvalRemoveSet(id: number | string) {
  return request<API.UnifiedResponse<unknown>>(`/admin/cert-eval/sets/${id}`, {
    method: 'DELETE',
  });
}

export async function certEvalBatchRemoveSets(body: { ids: Array<number | string> }) {
  return request<API.UnifiedResponse<number>>('/admin/cert-eval/sets/batch-remove', {
    method: 'POST',
    data: body,
  });
}

export async function certEvalCreateRun(body: {
  setId: number | string;
  versionTag: string;
  recMode?: string;
  endpoint?: 'prod' | 'release';
  overwriteConfirmed?: boolean;
}) {
  return request<API.UnifiedResponse<any>>('/admin/cert-eval/runs', {
    method: 'POST',
    data: body,
  });
}

export async function certEvalPageRuns(params?: {
  page?: number;
  pageSize?: number;
  setId?: number | string;
  status?: string;
}) {
  return request<API.UnifiedResponse<{ list?: any[]; total?: number }>>('/admin/cert-eval/runs/page', {
    method: 'GET',
    params,
  });
}

export async function certEvalGetRun(id: number | string) {
  return request<API.UnifiedResponse<any>>(`/admin/cert-eval/runs/${id}`, {
    method: 'GET',
  });
}

export async function certEvalConfirmRun(id: number | string) {
  return request<API.UnifiedResponse<unknown>>(`/admin/cert-eval/runs/${id}/confirm`, {
    method: 'POST',
  });
}

export async function certEvalDiscardRun(id: number | string) {
  return request<API.UnifiedResponse<unknown>>(`/admin/cert-eval/runs/${id}/discard`, {
    method: 'POST',
  });
}

export async function certEvalRetryFailed(id: number | string) {
  return request<API.UnifiedResponse<unknown>>(`/admin/cert-eval/runs/${id}/retry-failed`, {
    method: 'POST',
  });
}

export async function certEvalCompare(runA: number | string, runB: number | string) {
  return request<API.UnifiedResponse<any>>('/admin/cert-eval/compare', {
    method: 'GET',
    params: { runA, runB },
  });
}

export async function certEvalExportCompare(
  body: {
    runA: number | string;
    runB: number | string;
    sampleIds: Array<number | string>;
    includeA?: boolean;
    includeB?: boolean;
    fieldKeys?: string[];
  },
  options?: { [key: string]: any },
) {
  return request<Blob>('/admin/cert-eval/compare/export', {
    method: 'POST',
    data: body,
    responseType: 'blob',
    ...(options || {}),
  });
}

/** 查询 OCR 证件识别服务版本 GET /ccb-ai/cert/version */
export async function certRecognizeVersion() {
  return request<API.UnifiedResponse<{ name?: string; version?: string }>>('/ccb-ai/cert/version', {
    method: 'GET',
  });
}

/** 管理员微信解绑权限由后端角色及 API 权限共同决定。 */
export async function getWechatUnbindPermission() {
  return request<API.UnifiedResponse<{ allowed: boolean }>>('/user/wechat/admin-permission', { method: 'GET' });
}

/** 强制解绑微信并撤销该用户小程序和 WAP 的登录凭证，保留管理端会话。 */
export async function forceUnbindWechat(userId: number, reason: string) {
  return request<API.UnifiedResponse>(`/users/${userId}/wechat/unbind`, {
    method: 'POST', data: { reason },
  });
}
